import { XIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Logo } from '@/components/layout/logo.atom'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

import { OnboardingBackground } from './onboarding-background.atom'
import { OnboardingVideo } from './onboarding-video.atom'
import type { OnboardingWizardProps } from './onboarding-wizard.interface'
import { OnboardingWizardForm } from './onboarding-wizard-form.molecule'

const ONBOARDING_VIDEO_ID = '_mWPo1Pd8os'

/**
 * Full-screen onboarding wizard overlay for new users.
 * Frosted-glass fixed overlay on desktop (no scroll), scrollable on mobile.
 * Provides skip and submit callbacks — no business logic inside.
 */
export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  isOpen,
  onClose,
  onSkip,
  onSubmit,
  isLoading = false,
  toolNameList,
  defaultWebsiteUrl,
  className,
}): ReactNode => {
  if (!isOpen) {
    return null
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to SAAGA"
      className={cn(
        'onboarding-wizard fixed inset-0 z-50 bg-background',
        'lg:overflow-hidden',
        className
      )}
    >
      <OnboardingBackground toolNameList={toolNameList} />

      <div className="relative z-10 hidden h-full flex-col lg:flex">
        <OnboardingWizardHeader onClose={onClose} onSkip={onSkip} isLoading={isLoading} />
        <OnboardingWizardBody
          onSubmit={onSubmit}
          isLoading={isLoading}
          defaultWebsiteUrl={defaultWebsiteUrl}
        />
      </div>

      <ScrollArea className="relative z-10 h-dvh lg:hidden">
        <div className="onboarding-wizard-content relative flex min-h-dvh flex-col">
          <OnboardingWizardHeader onClose={onClose} onSkip={onSkip} isLoading={isLoading} />
          <OnboardingWizardBody
            onSubmit={onSubmit}
            isLoading={isLoading}
            defaultWebsiteUrl={defaultWebsiteUrl}
          />
        </div>
      </ScrollArea>
    </div>
  )
}

interface OnboardingWizardHeaderProps {
  onClose: () => void
  onSkip: () => void
  isLoading: boolean
}

const OnboardingWizardHeader: React.FC<OnboardingWizardHeaderProps> = ({
  onClose,
  onSkip,
  isLoading,
}): ReactNode => (
  <div className="onboarding-wizard-header pointer-events-none absolute inset-x-0 top-0 z-20 flex items-start justify-between px-6 pt-6 sm:px-10 sm:pt-8">
    <div className="pointer-events-auto rounded-2xl bg-white px-5 py-3 shadow-lg dark:bg-background">
      <Logo className="h-6 w-auto" />
    </div>
    <div className="pointer-events-auto flex items-center gap-2">
      <div className="rounded-2xl bg-white shadow-lg dark:bg-background">
        <Button
          variant="ghost"
          onClick={onSkip}
          disabled={isLoading}
          className="onboarding-wizard-skip-button rounded-2xl text-muted-foreground opacity-60 hover:opacity-100"
        >
          Skip for now
        </Button>
      </div>
      <div className="rounded-2xl bg-white shadow-lg dark:bg-background">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="onboarding-wizard-close-button rounded-2xl text-muted-foreground opacity-60 hover:opacity-100"
        >
          <XIcon className="size-4" />
        </Button>
      </div>
    </div>
  </div>
)

interface OnboardingWizardBodyProps {
  onSubmit: OnboardingWizardProps['onSubmit']
  isLoading: boolean
  defaultWebsiteUrl?: string
}

const OnboardingWizardBody: React.FC<OnboardingWizardBodyProps> = ({
  onSubmit,
  isLoading,
  defaultWebsiteUrl,
}): ReactNode => (
  <div className="flex flex-1 items-center justify-center px-6 py-10 sm:px-10">
    <div className="onboarding-wizard-main w-full max-w-4xl rounded-3xl bg-white p-8 shadow-2xl sm:p-12 dark:bg-background">
      <div className="space-y-2 text-center">
        <h2 className="text-4xl font-bold tracking-tight sm:text-6xl">Welcome to SAAGA Solve</h2>
        <p className="text-lg text-muted-foreground sm:text-2xl">
          Let&apos;s check something. Maybe your website? Or your client website? Just need few
          details and we are 🚀 Ready to see the magic?
        </p>
      </div>

      <div className="mt-8 flex flex-col items-stretch gap-8 lg:flex-row">
        <div className="w-full lg:w-1/2">
          <OnboardingVideo videoId={ONBOARDING_VIDEO_ID} className="h-full" />
        </div>
        <div className="w-full lg:w-1/2">
          <OnboardingWizardForm
            onSubmit={onSubmit}
            isLoading={isLoading}
            defaultWebsiteUrl={defaultWebsiteUrl}
            className="h-full"
          />
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        This helps us tailor suggestions and insights for your business. You can update these
        details anytime.
      </p>
    </div>
  </div>
)
